import { useTranslation } from 'react-i18next';
import { EditableSection } from './EditableSection.js';
import { FormatHint } from './FormatHint.js';
import { SpecTable } from './SpecTable.js';
import { ValidationListEditor } from './ValidationListEditor.js';
import type { SpecValidationList, SpecValidationRow } from '../../../lib/api/spec-requirements.js';

export interface ValidationListSectionProps {
  /** 今保存されているバリデーション一覧。無い案件では null */
  validationList: SpecValidationList | null;
  /** この節を開いているか */
  editing: boolean;
  /** 直前の保存で残った注意（内部名・文言の書き方） */
  warnings: string[];
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因 */
  error: unknown;
  /** 編集を開く */
  onEdit: () => void;
  /** 保存する */
  onSave: (rows: SpecValidationRow[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
  /** レビュー操作。呼び出し側が組み立てるので、ここでは `ReviewActions` を知らない */
  actions: React.ReactNode;
}

/**
 * バリデーション一覧の節。読むときは表、編集するときはフォーム。
 *
 * 応答ステータス・エラーコード・ログの文言は基本設計で決めるので列に持たない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バリデーション一覧（画面・表示位置・条件・メッセージ）を読み、編集して保存し、レビュー操作を並べる節
 * @graph-connects ValidationListEditor [calls] バリデーション一覧の編集
 */
export function ValidationListSection({
  validationList,
  editing,
  warnings,
  saving,
  error,
  onEdit,
  onSave,
  onCancel,
  actions,
}: ValidationListSectionProps) {
  const { t } = useTranslation();

  return (
    <EditableSection
      version={validationList?.version}
      heading={t('spec.artifact.validationList')}
      action={t(validationList === null ? 'spec.action.write' : 'spec.action.edit')}
      editing={editing}
      warnings={warnings}
      onEdit={onEdit}
    >
      {editing ? (
        <FormatHint artifact="validation_list" label={t('spec.artifact.validationList')} />
      ) : null}
      {editing ? (
        <ValidationListEditor
          validationList={validationList}
          saving={saving}
          error={error}
          warnings={warnings}
          onSave={onSave}
          onCancel={onCancel}
        />
      ) : validationList === null ? (
        <p className="mt-3 text-sm text-[var(--color-text-muted)]">
          {t('spec.validationList.empty')}
        </p>
      ) : (
        <>
          {/* 訳が無い・古いときは原文の行が返る。空で隠されていないので、
            読んでいるものが原文だと分かるように断る */}
          {validationList?.untranslated === true ? (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.validationList.untranslated')}
            </p>
          ) : null}
          <SpecTable
            numbered={{ prefix: 'V', heading: t('spec.section.rowNumber') }}
            headings={[
              t('spec.validationList.columns.screen'),
              t('spec.validationList.columns.position'),
              t('spec.validationList.columns.condition'),
              t('spec.validationList.columns.message'),
            ]}
            rows={validationList.rows.map((row) => ({
              key: `${row.screen}/${row.condition}`,
              no: row.no,
              cells: [row.screen, row.position, row.condition, row.message],
            }))}
          />
        </>
      )}
      {actions}
    </EditableSection>
  );
}
